import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Users, Repeat, Map, Radar, Package, BarChart3, Bell, Settings, ShieldCheck,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { BrandLogo } from "../shared/BrandLogo";
import { useAuth } from "../../context/AuthContext";

export const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "sidebar-dashboard-link", perm: "dashboard.view" },
  { to: "/customers", label: "Customers", icon: Users, testId: "sidebar-customers-link", perm: "customers.view" },
  { to: "/subscriptions", label: "Subscriptions", icon: Repeat, testId: "sidebar-subscriptions-link", perm: "subscriptions.view" },
  { to: "/routes", label: "Routes & Drivers", icon: Map, testId: "sidebar-routes-link", perm: "routes.view" },
  { to: "/live-map", label: "Live Map", icon: Radar, testId: "sidebar-live-map-link", perm: "live_map.view" },
  { to: "/catalog", label: "Catalog", icon: Package, testId: "sidebar-catalog-link", perm: "catalog.view" },
  { to: "/reports", label: "Reports", icon: BarChart3, testId: "sidebar-reports-link", perm: "reports.view" },
  { to: "/notifications", label: "Notifications", icon: Bell, testId: "sidebar-notifications-link", perm: "notifications.view" },
  { to: "/settings", label: "Settings", icon: Settings, testId: "sidebar-settings-link", perm: "settings.view" },
  { to: "/admin-management", label: "Admin Management", icon: ShieldCheck, testId: "sidebar-admin-management-link", superOnly: true },
];

export function Sidebar() {
  // Collapsed to an icon-only rail by default; slides out fully on hover and
  // collapses again on mouse-leave or after navigating to a page.
  const [expanded, setExpanded] = useState(false);
  const { user, hasPerm } = useAuth();
  const items = NAV_ITEMS.filter((it) =>
    it.superOnly ? !!user?.is_super_admin : (!it.perm || hasPerm(it.perm)));

  return (
    // The rail reserves a fixed 64px; the panel overlays content when expanded
    // so the page doesn't reflow.
    <aside
      className="relative hidden w-16 flex-shrink-0 print:hidden md:block"
      data-testid="app-sidebar"
      data-expanded={expanded}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      <div
        className={cn(
          "absolute inset-y-0 left-0 z-50 flex h-screen flex-col overflow-hidden border-r border-primary-dark bg-primary text-white transition-[width] duration-200 ease-out",
          expanded ? "w-64 shadow-2xl" : "w-16"
        )}
      >
        <div className="flex h-16 min-h-[64px] items-center gap-2 overflow-hidden px-3">
          {expanded ? (
            <BrandLogo className="h-7 pl-1" />
          ) : (
            <img
              src="/branding/badge_logo.png"
              alt="The Virsa Tiffin"
              className="mx-auto h-9 w-9 select-none object-contain"
              draggable={false}
              data-testid="brand-logo-badge"
            />
          )}
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-2 py-4">
          {items.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              title={label}
              onClick={() => setExpanded(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md border-l-2 border-transparent px-3 py-2.5 text-sm font-medium text-white/80 transition-colors hover:bg-primary-hover hover:text-white",
                  isActive && "border-accent bg-primary-hover text-white"
                )
              }
            >
              <Icon size={18} strokeWidth={1.5} className="flex-shrink-0" />
              <span
                className={cn(
                  "truncate whitespace-nowrap transition-opacity duration-150",
                  expanded ? "opacity-100" : "opacity-0"
                )}
              >
                {label}
              </span>
            </NavLink>
          ))}
        </nav>
        <div
          className={cn(
            "overflow-hidden whitespace-nowrap border-t border-white/10 px-5 py-4 text-[11px] text-white/50 transition-opacity duration-150",
            expanded ? "opacity-100" : "opacity-0"
          )}
        >
          The Virsa Tiffin Admin · Phase 3
        </div>
      </div>
    </aside>
  );
}
